import { Injectable } from '@angular/core';
import { Issue } from '../issue/issue.dto';
import { getIssueDescription, NoteService } from './note.service';
import { logger, ChangeTyped, IndexedService, NOTES_STORE } from '@damofire/data-commons';
import { ServiceEventType } from '@damofire/data-angular-commons';
import { Note } from './note.dto';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';
import { AuthDtoSignerService } from '@damofire/data-security';
import { Asset } from '../asset/asset.dto';
import { Inspection } from '../inspection/inspection.dto';
import { getEmbeddedAsset } from '../asset/asset.service';
import { cloneDeep } from 'lodash-es';

@Injectable()
export class IdbNoteService extends NoteService {
  constructor(
    private readonly idbService: IndexedService,
    private readonly warehouseSyncEventService: WarehouseSyncEventService,
    private readonly dtoSigner: AuthDtoSignerService,
  ) {
    super();
  }

  async createForAsset(note: string, asset: Asset, inspection: Inspection): Promise<void> {
    const create: Note = {
      _id: new Date().getTime().toString(),
      text: note,
      inspection: { _id: inspection._id.toString(), name: inspection.name },
      asset: getEmbeddedAsset(asset),
      division: inspection.division,
      organization: inspection.organization,
    };
    await this.createNote(create);
  }

  async createForIssue(note: string, issue: Issue): Promise<void> {
    if (!issue.inspection) {
      throw new Error('Missing inspection');
    }

    const create = {
      _id: this.dtoSigner.generateId(),
      text: note,
      inspection: { _id: issue.inspection._id.toString(), name: issue.inspection.name },
      asset: { ...issue.asset, _id: issue.asset._id.toString() },
      issue: { _id: issue._id, ...getIssueDescription(issue), warehouseIssueId: issue.warehouseIssueId },
      division: issue.division,
      organization: issue.organization,
    };
    await this.createNote(create);
  }

  async createForIssueBatch(notes: string[], issue: Issue): Promise<void> {
    if (!issue.inspection) {
      throw new Error('Missing inspection');
    }

    const create: Partial<Note> = {
      inspection: { _id: issue.inspection._id.toString(), name: issue.inspection.name },
      asset: { ...issue.asset, _id: issue.asset._id.toString() },
      issue: { _id: issue._id, ...getIssueDescription(issue), warehouseIssueId: issue.warehouseIssueId },
      division: issue.division,
      organization: issue.organization,
    };
    await this.dtoSigner.sign(create, true);

    const notesToCreate: Note[] = notes.map((note) => {
      // Ensure that the notes dot not have the exact same creation date to keep the order
      (create.audit.createdDate as Date).setSeconds((create.audit.createdDate as Date).getSeconds() + 1);
      (create.audit?.lastModifiedDate as Date).setSeconds((create.audit.lastModifiedDate as Date).getSeconds() + 1);
      return { ...create, text: note, _id: this.dtoSigner.generateId(), audit: cloneDeep(create.audit) };
    });

    logger.info('Creating idb notes', { count: notesToCreate.length, category: 'note' });
    await this.idbService.addAll(NOTES_STORE, notesToCreate);

    const changesTyped: ChangeTyped<Note>[] = notesToCreate.map((note) => ({ ...note, changeType: 'added' }));
    this.warehouseSyncEventService.publishOutput({
      division: issue.division._id,
      changes: changesTyped,
      store: NOTES_STORE,
    });
    super.publishEvent(ServiceEventType.CREATED, ...notesToCreate);
  }

  private async createNote(note: Note): Promise<void> {
    await this.dtoSigner.sign(note, true);
    logger.info('Creating idb note', { id: note._id, category: 'note' });
    await this.idbService.add(NOTES_STORE, note);

    const changeTyped: ChangeTyped<Note> = { ...note, changeType: 'added' };
    this.warehouseSyncEventService.publishOutput({
      division: note.division._id,
      changes: [changeTyped],
      store: NOTES_STORE,
    });
    super.publishEvent(ServiceEventType.CREATED, note);
  }

  async delete(division: string, id: string): Promise<void> {
    await this.idbService.delete(NOTES_STORE, [division, id]).catch(console.error);
    this.warehouseSyncEventService.publishOutput({
      division,
      changes: [{ _id: id, changeType: 'removed' } as ChangeTyped<Note>],
      store: NOTES_STORE,
    });
  }

  async update(note: Note, text: string): Promise<void> {
    const updated = { ...note, text };
    await this.idbService.update(NOTES_STORE, updated).catch(console.error);
    this.warehouseSyncEventService.publishOutput({
      division: note.division._id,
      changes: [{ ...updated, changeType: 'modified' } as ChangeTyped<Note>],
      store: NOTES_STORE,
    });
  }
}
